import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  IconButton,
} from "@mui/material";
import { useState } from "react";
import { Link, useLoaderData, useNavigate } from "react-router-dom";

import AddIcon from "@mui/icons-material/Add";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SyncIcon from "@mui/icons-material/Sync";
import { toast } from "react-toastify";

import DefaultApiService from "../../../../../infrastructure/api/default";
import useCheckPermission from "../../../../common/hooks/useCheckPermission";
import useGetPageInfo from "../../../../common/hooks/useGetPageInfo";
import DeleteConfirm from "../../../../components/shared/Dialog/DeleteConfirm";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import EnhancedTable from "../../../../components/shared/table";

const API_BASE = "BI/Administration/SolarentOfficialBankAccount";

const SolarentOfficialBankAccountPage = () => {
  const accountList = useLoaderData("root") || [];
  const pageInfo = useGetPageInfo();
  const navigate = useNavigate();
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, data: {} });

  const headCells = [
    { id: "title", label: "عنوان" },
    { id: "bankName", label: "بانک" },
    { id: "bankAccountNumber", label: "شماره حساب" },
    { id: "bankShebaNumber", label: "شماره شبا" },
    {
      id: "isActive",
      label: "وضعیت",
      type: "boolean",
      render: (value) =>
        value ? (
          <Chip size="small" color="success" label="فعال در قرارداد" />
        ) : (
          <Chip size="small" label="غیرفعال" />
        ),
    },
    { id: "updateOn", label: "آخرین بروزرسانی", type: "date" },
  ];

  const onEditClicked = (row) => navigate(`${row.id}`);

  const onActivateClicked = async (row) => {
    if (row.isActive) return;
    try {
      const res = await DefaultApiService.postApi(`${API_BASE}/Activate`, {
        id: row.id,
      });
      if (res?.isSuccess) {
        toast.success(res.message || "حساب فعال شد.");
        navigate();
      } else {
        toast.error(res?.message || "فعال‌سازی انجام نشد.");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "خطایی رخ داده است.");
    }
  };

  const onDeleteClicked = (row) => {
    if (row.isActive) {
      toast.warning("ابتدا حساب دیگری را فعال کنید، سپس این حساب را حذف کنید.");
      return;
    }
    setDeleteConfirm({ open: true, data: row });
  };

  const confirmedDelete = () =>
    DefaultApiService.deleteOneApi(API_BASE, { id: deleteConfirm.data.id });

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <BreadcrumbsComponent />
        <DeleteConfirm
          message={`حذف حساب «${deleteConfirm.data.title || deleteConfirm.data.bankName}»؟`}
          open={deleteConfirm.open}
          onClose={(loadAgain) => {
            setDeleteConfirm({ open: false, data: {} });
            if (loadAgain) navigate();
          }}
          onSubmit={confirmedDelete}
        />
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardHeader
            title={pageInfo?.title || "حساب‌های بانکی سولارنت"}
            subheader="فقط یک حساب می‌تواند در قرارداد متقاضی درج شود."
            action={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {useCheckPermission("create") && (
                  <Link to="create">
                    <Button variant="contained" startIcon={<AddIcon />}>
                      حساب جدید
                    </Button>
                  </Link>
                )}
                <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                <IconButton onClick={() => navigate()}>
                  <SyncIcon />
                </IconButton>
              </Box>
            }
          />
          <CardContent>
            <EnhancedTable
              rowKey="id"
              rows={accountList}
              headCells={headCells}
              pageSize={100}
              actions={[
                {
                  icon: useCheckPermission("edit") ? (
                    <EditIcon sx={{ width: "1rem", height: "1rem" }} />
                  ) : null,
                  onClick: onEditClicked,
                  color: "primary",
                },
                {
                  icon: useCheckPermission("edit") ? (
                    <CheckCircleIcon sx={{ width: "1rem", height: "1rem" }} />
                  ) : null,
                  onClick: onActivateClicked,
                  color: "success",
                  title: "فعال برای قرارداد",
                },
                {
                  icon: useCheckPermission("delete") ? (
                    <DeleteIcon sx={{ width: "1rem", height: "1rem" }} />
                  ) : null,
                  onClick: onDeleteClicked,
                  color: "error",
                  title: "حذف",
                },
              ]}
            />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default SolarentOfficialBankAccountPage;
